/* eslint-disable react/jsx-props-no-spreading */
/** @jsxImportSource @emotion/react */
import { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import getParentCategoriesList from "api/getParentCategoriesList";
import getCategoriesList from "api/getCategoriesList";
import Select from "components/common/Select";
import { URL_KEYS } from "constants/dashboard";
import parseErrorMessage from "helpers/parseErrorMessage";
import useEvent from "hooks/useEvent";
import useRouterSearchParams from "hooks/useRouterSearchParams";
import { categoriesListParamsKey } from "queryKeys/categoriesListKey";
import { parentCategoriesListParamsKey } from "queryKeys/parentCategoriesListKey";
import STYLES from "./index.css";
import transformOptions from "./transformOptions";
import { ADDITIONAL, DEBOUNCE_TIMEOUT, PER_PAGE } from "./constants";

const QUERY_PARAMS_DICTIONARY = {
  parentCategory: {
    queryFn: getParentCategoriesList,
    queryKey: parentCategoriesListParamsKey,
  },

  category: {
    queryFn: getCategoriesList,
    queryKey: categoriesListParamsKey,
  },
};

export default function CategoryFilter({ filterName, data, ...props }) {
  const queryClient = useQueryClient();

  const { queryFn, queryKey } = QUERY_PARAMS_DICTIONARY[filterName];

  const [searchParams, setSearchParams] = useRouterSearchParams();
  const searchValue = searchParams.get(`${filterName}Id`) ?? null;
  const searchValueName = searchParams.get(`${filterName}Name`) ?? null;

  const initOptions =
    searchValue && searchValueName
      ? [
          {
            label: searchValueName,
            value: searchValue,
          },
        ]
      : [];

  const [{ isLoading, options }, setOptions] = useState({
    isLoading: false,
    options: initOptions,
  });

  const selected = useMemo(
    () => options?.find((option) => option.value?.toString() === searchValue),
    [options, searchValue],
  );

  const params = useMemo(() => new URLSearchParams(searchParams), [searchParams]);

  const handleChange = useEvent((option, { action }) => {
    const value = option?.value;
    const optionLabel = option?.label;

    if (value === selected?.value) return;

    if (action === "clear") {
      params.delete(`${filterName}Id`);
      params.delete(`${filterName}Name`, optionLabel);
    } else {
      params.set(`${filterName}Id`, value);
      params.set(`${filterName}Name`, optionLabel);

      if (filterName === "parentCategory") {
        params.delete("categoryName");
        params.delete("categoryId");
      }

      if (params.has(URL_KEYS.page)) {
        params.delete(URL_KEYS.page);
      }
    }

    setSearchParams(params);
  });

  const loadOptions = useCallback(
    (search, loadedOptions, { page }) => {
      const offset = page > 0 ? page * PER_PAGE : 0;
      const queryParams = {
        page,
        perPage: PER_PAGE,
        search: search || undefined,
      };

      if (filterName === "category") {
        queryParams.parentCategoryId = Number(searchParams.get("parentCategoryId")) || undefined;
      }

      setOptions((prevState) => ({ ...prevState, isLoading: true }));

      return queryClient
        .fetchQuery({ queryKey: queryKey({ queryParams }), queryFn })
        .then(({ data: newData }) => {
          const newOptions = newData?.data;
          const total = newData?.total;

          const transformedOptions = transformOptions(newOptions);
          setOptions((prevState) => ({
            isLoading: false,
            options: [...prevState.options, ...transformedOptions],
          }));

          return {
            options: transformedOptions,
            hasMore: total > offset,
            additional: {
              page: page + 1,
            },
          };
        })
        .catch((error) => {
          toast.error(parseErrorMessage(error));

          setOptions((prevState) => ({ ...prevState, isLoading: false }));

          return {
            options: loadedOptions,
            hasMore: false,
            additional: {
              page,
            },
          };
        });
    },
    [filterName, queryClient, queryFn, queryKey, searchParams],
  );

  return (
    <Select
      label="Category"
      variant="outlined"
      size="lg"
      styles={STYLES}
      placeholder={isLoading ? "Loading..." : "Select"}
      value={selected ? [selected] : []}
      onChange={handleChange}
      loadOptions={loadOptions}
      debounceTimeout={DEBOUNCE_TIMEOUT}
      additional={ADDITIONAL}
      selectComponent="async"
      hideSelectedOptions
      isClearable
      {...props}
    />
  );
}

CategoryFilter.propTypes = {
  filterName: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})),
};
